import type { User } from "@/shared/types/auth.types";
import { FeatureToggleSource } from "./feature.types";
import type { FeatureFlag, FeatureToggleResult } from "./feature.types";
import { UserBucketService } from "./userBucket.service";

// Feature flag evaluation service
export class FeatureFlagEvaluator {
  private static instance: FeatureFlagEvaluator;
  private userBucketService = UserBucketService.getInstance();

  static getInstance(): FeatureFlagEvaluator {
    if (!FeatureFlagEvaluator.instance) {
      FeatureFlagEvaluator.instance = new FeatureFlagEvaluator();
    }
    return FeatureFlagEvaluator.instance;
  }

  // Evaluate a feature flag with rollout logic
  evaluateFlag(flag: FeatureFlag, user: User): FeatureToggleResult {
    // Check if feature is globally enabled
    if (!flag.enabled) {
      return {
        enabled: false,
        reason: "Feature globally disabled",
        source: FeatureToggleSource.Global,
      };
    }

    // Check date restrictions
    const dateResult = this.checkDateRestrictions(flag);
    if (!dateResult.enabled) {
      return dateResult;
    }

    // Check deny list
    if (flag.denyList?.includes(user.id)) {
      return {
        enabled: false,
        reason: "User in deny list",
        source: FeatureToggleSource.Global,
      };
    }

    // Check allow list (overrides other restrictions)
    if (flag.allowList?.includes(user.id)) {
      return {
        enabled: true,
        reason: "User in allow list",
        source: FeatureToggleSource.Global,
      };
    }

    // Check role targeting
    const roleResult = this.checkRoleTargeting(flag, user);
    if (!roleResult.enabled) {
      return roleResult;
    }

    // Check rollout percentage
    const rolloutResult = this.checkRolloutPercentage(flag, user);
    return rolloutResult;
  }

  private checkDateRestrictions(flag: FeatureFlag): FeatureToggleResult {
    const now = new Date();

    if (flag.startDate && now < flag.startDate) {
      return {
        enabled: false,
        reason: "Feature not yet active",
        source: FeatureToggleSource.Global,
      };
    }

    if (flag.endDate && now > flag.endDate) {
      return {
        enabled: false,
        reason: "Feature expired",
        source: FeatureToggleSource.Global,
      };
    }

    return { enabled: true, source: FeatureToggleSource.Global };
  }

  private checkRoleTargeting(
    flag: FeatureFlag,
    user: User,
  ): FeatureToggleResult {
    if (!flag.targetRoles?.length) {
      return { enabled: true, source: FeatureToggleSource.Global };
    }

    const hasRequiredRole = flag.targetRoles.some((role) =>
      user.roles?.includes(role),
    );

    if (!hasRequiredRole) {
      return {
        enabled: false,
        reason: `Feature not available for role(s): ${user.roles?.join(", ")}`,
        source: FeatureToggleSource.Global,
      };
    }

    return { enabled: true, source: FeatureToggleSource.Global };
  }

  private checkRolloutPercentage(
    flag: FeatureFlag,
    user: User,
  ): FeatureToggleResult {
    if (flag.rolloutPercentage === undefined || flag.rolloutPercentage >= 100) {
      return { enabled: true, source: FeatureToggleSource.Global };
    }

    const inRollout = this.userBucketService.isUserInRollout(
      user.id,
      flag.key,
      flag.rolloutPercentage,
    );

    if (!inRollout) {
      return {
        enabled: false,
        reason: `User not in rollout (${flag.rolloutPercentage}%)`,
        source: FeatureToggleSource.Global,
      };
    }

    return { enabled: true, source: FeatureToggleSource.Global };
  }
}
